/**
 * Histórico de movimientos de inventario (entradas o salidas).
 *
 * Las dos pantallas eran casi idénticas en el panel anterior
 * (`views/menu/inventario/entrada.php` y `salida.php`): una tabla con fecha,
 * usuario, producto, tipo, cantidad, descripción y el botón de **anular** el
 * movimiento (borrado lógico que devuelve el stock a su sitio). Aquí se resuelven
 * con el mismo componente y sólo cambian el título, los filtros y las columnas.
 */

import { Filter, RotateCcw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { CampoFecha } from '@/components/ui/calendario';
import { ApiError } from '@/lib/http/api-error';
import { formatNumber } from '@/lib/utils';

import { useAnularEntrada, useAnularSalida, useInventarioEntradas, useInventarioSalidas } from '../inventario.api';
import type { MovimientoInventario } from '../gestion.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Fecha y hora legibles. */
function fechaHora(valor?: string | null): string {
  if (!valor) return '—';

  const fecha = new Date(valor);

  if (Number.isNaN(fecha.getTime())) return valor;

  return fecha.toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' });
}

interface InventarioMovimientosProps {
  tipo: 'entrada' | 'salida';
  /** Productos del inventario, para el filtro y el resumen. */
  productos: { id: number; nombre: string; unidad: string }[];
}

export function InventarioMovimientos({ tipo, productos }: InventarioMovimientosProps) {
  const esEntrada = tipo === 'entrada';
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [productoId, setProductoId] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [aAnular, setAAnular] = useState<MovimientoInventario | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const filtros = useMemo(
    () => ({
      desde: desde || undefined,
      hasta: hasta || undefined,
      producto_id: productoId ? Number(productoId) : undefined,
      search: busqueda || undefined,
    }),
    [busqueda, desde, hasta, productoId],
  );

  // Los dos hooks se llaman siempre (regla de hooks) y se pide sólo el que toca.
  const entradas = useInventarioEntradas(filtros, esEntrada);
  const salidas = useInventarioSalidas(filtros, !esEntrada);
  const consulta = esEntrada ? entradas : salidas;

  const anularEntrada = useAnularEntrada();
  const anularSalida = useAnularSalida();
  const anular = esEntrada ? anularEntrada : anularSalida;

  const movimientos = consulta.data?.movimientos ?? [];

  return (
    <div className="space-y-4">
      {aviso ? (
        <Alert variant="success" title="Listo">
          <span className={ICONO_EN_ALERTA}>
            <RotateCcw className="size-4" aria-hidden="true" />
            {aviso}
          </span>
        </Alert>
      ) : null}

      <Card>
        <CardContent className="space-y-4">
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1.5">
                <label htmlFor={`mov-desde-${tipo}`} className="text-xs font-semibold text-ink-500">
                  Desde
                </label>
                <CampoFecha
                  id={`mov-desde-${tipo}`}
                 
                  className={`${CONTROL} h-10 w-40`}
                  value={desde}
                  onChange={(evento) => setDesde(evento.target.value)}
                 tipo="date" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor={`mov-hasta-${tipo}`} className="text-xs font-semibold text-ink-500">
                  Hasta
                </label>
                <CampoFecha
                  id={`mov-hasta-${tipo}`}
                 
                  className={`${CONTROL} h-10 w-40`}
                  value={hasta}
                  min={desde || undefined}
                  onChange={(evento) => setHasta(evento.target.value)}
                 tipo="date" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor={`mov-producto-${tipo}`}
                  className="text-xs font-semibold text-ink-500"
                >
                  Producto
                </label>
                <Select
                  id={`mov-producto-${tipo}`}
                  className={`${CONTROL} h-10 w-56`}
                  value={productoId}
                  onChange={(evento) => setProductoId(evento.target.value)}
                >
                  <option value="">Todos los productos</option>
                  {productos.map((producto) => (
                    <option key={producto.id} value={producto.id}>
                      {producto.nombre}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex min-w-52 flex-1 flex-col gap-1.5">
                <label htmlFor={`mov-buscar-${tipo}`} className="text-xs font-semibold text-ink-500">
                  Buscar
                </label>
                <div className="relative">
                  <Search
                    className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-300"
                    aria-hidden="true"
                  />
                  <input
                    id={`mov-buscar-${tipo}`}
                    className={`${CONTROL} h-10 pl-9`}
                    placeholder="Producto, descripción o usuario…"
                    value={busqueda}
                    onChange={(evento) => setBusqueda(evento.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">
                {formatNumber(consulta.data?.total ?? 0)} movimientos
              </Badge>
              <Badge variant={esEntrada ? 'success' : 'warning'}>
                {formatNumber(consulta.data?.cantidad ?? 0)} {esEntrada ? 'ingresados' : 'retirados'}
              </Badge>
              <Button
                variant="ghost"
                onClick={() => {
                  setDesde('');
                  setHasta('');
                  setProductoId('');
                  setBusqueda('');
                }}
                disabled={!desde && !hasta && !productoId && !busqueda}
              >
                <Filter className="mr-1.5 size-3.5" aria-hidden="true" />
                Limpiar
              </Button>
            </div>
          </header>

          {consulta.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : consulta.error ? (
            <Alert variant="warning" title="No se pudieron leer los movimientos">
              <span className={ICONO_EN_ALERTA}>
                <RotateCcw className="size-4" aria-hidden="true" />
                {mensajeDe(consulta.error, 'La API no devolvió los movimientos.')}
              </span>
            </Alert>
          ) : movimientos.length === 0 ? (
            <p className="text-sm text-ink-500">
              No hay {esEntrada ? 'entradas' : 'salidas'} con los filtros actuales.
            </p>
          ) : (
            <div className="max-h-[58vh] overflow-auto rounded-control border border-ink-50">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2 pl-3">Fecha</th>
                    <th className="py-2">Usuario</th>
                    <th className="py-2">Producto</th>
                    <th className="py-2">Tipo</th>
                    <th className="py-2 text-right">Cantidad</th>
                    <th className="py-2">Descripción</th>
                    {esEntrada ? <th className="py-2">Comprobante</th> : null}
                    <th className="w-20 py-2 pr-3 text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map((movimiento) => (
                    <tr key={movimiento.detalle_id} className="border-b border-ink-50 last:border-0">
                      <td className="py-2 pl-3 text-ink-700">{fechaHora(movimiento.fecha)}</td>
                      <td className="py-2 text-ink-500">{movimiento.usuario}</td>
                      <td className="py-2 text-ink-700">{movimiento.producto}</td>
                      <td className="py-2">
                        <Badge variant="outline">{movimiento.tipo_producto}</Badge>
                      </td>
                      <td className="py-2 text-right font-medium text-ink-900">
                        {formatNumber(movimiento.cantidad)}{' '}
                        <span className="text-xs text-ink-400">{movimiento.unidad}</span>
                      </td>
                      <td className="py-2 text-ink-500">{movimiento.descripcion}</td>
                      {esEntrada ? (
                        <td className="py-2 text-ink-500">
                          {movimiento.factura ?? '—'}
                          {movimiento.proveedor ? (
                            <span className="block text-[11px] text-ink-400">
                              {movimiento.proveedor}
                            </span>
                          ) : null}
                        </td>
                      ) : null}
                      <td className="py-2 pr-3 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setAAnular(movimiento)}
                          disabled={anular.isPending}
                          title="Anular el movimiento y devolver el stock"
                          aria-label={`Anular ${esEntrada ? 'la entrada' : 'la salida'} de ${movimiento.producto}`}
                        >
                          <RotateCcw className="size-3.5 text-rose-600" aria-hidden="true" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-ink-400">
            Anular un movimiento es un <strong>borrado lógico</strong>: deja de contar en el listado
            y devuelve (o quita) la cantidad del stock del producto, como el panel anterior.
          </p>
        </CardContent>
      </Card>

      {aAnular ? (
        <ConfirmDialog
          open
          onClose={() => setAAnular(null)}
          onConfirm={() =>
            anular.mutate(aAnular.detalle_id, {
              onSuccess: () => {
                setAviso(
                  `${esEntrada ? 'La entrada' : 'La salida'} de ${formatNumber(aAnular.cantidad)} ${aAnular.unidad} de ${aAnular.producto} quedó anulada.`,
                );
                setAAnular(null);
              },
              onError: (error) =>
                setAviso(mensajeDe(error, 'No se pudo anular el movimiento.')),
            })
          }
          title={`¿Anular ${esEntrada ? 'la entrada' : 'la salida'}?`}
          description={`Se devolverá el stock del producto (${formatNumber(aAnular.cantidad)} ${aAnular.unidad} de ${aAnular.producto}) y el movimiento dejará de aparecer en el listado.`}
          confirmLabel="Anular"
        />
      ) : null}
    </div>
  );
}
